import { supabase } from "@/integrations/supabase/client";
import { isActivityPubliclyVisible } from "@/lib/events/publicVisibility";
import type { FavoriteItem } from "./types";
import {
  loadLocalFavorites,
  saveLocalFavorites,
  setLastSyncAt,
} from "./localFavorites";

type FavoriteRow = {
  kind: string;
  entity_id: string;
  slug: string | null;
  title: string | null;
  image_path: string | null;
  created_at: string;
};

async function fetchServerFavorites(userId: string): Promise<FavoriteRow[]> {
  const { data, error } = await supabase
    .from("favorites")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Failed to fetch server favorites", error);
    throw error;
  }

  return (data || []) as FavoriteRow[];
}

/**
 * Merges local favorites with server and returns the canonical list.
 * Only call when user is authenticated.
 */
export async function syncFavorites(userId: string): Promise<FavoriteItem[]> {
  // 1. Fetch server favorites
  const serverRows = await fetchServerFavorites(userId);

  const serverMap = new Map(
    serverRows.map((r) => [`${r.kind}:${r.entity_id}`, r])
  );

  // 2. Load local
  const local = loadLocalFavorites();

  // 3. Process local → server
  const upserts: Array<{
    user_id: string;
    kind: string;
    entity_id: string;
    slug: string | null;
    title: string | null;
    image_path: string | null;
  }> = [];
  const deletes: Array<{ kind: string; entityId: string }> = [];

  for (const item of local) {
    const key = `${item.kind}:${item.entityId}`;
    const onServer = serverMap.has(key);

    if (item.deletedAt) {
      if (onServer) {
        deletes.push({ kind: item.kind, entityId: item.entityId });
      }
    } else if (!onServer) {
      upserts.push({
        user_id: userId,
        kind: item.kind,
        entity_id: item.entityId,
        slug: item.slug ?? null,
        title: item.title ?? null,
        image_path: item.imagePath ?? null,
      });
    }
  }

  if (upserts.length > 0) {
    const { error: upsertError } = await supabase
      .from("favorites")
      .upsert(upserts, { onConflict: "user_id,kind,entity_id" });
    if (upsertError) console.error("Favorites upsert error", upsertError);
  }

  for (const { kind, entityId } of deletes) {
    await supabase
      .from("favorites")
      .delete()
      .eq("user_id", userId)
      .eq("kind", kind)
      .eq("entity_id", entityId);
  }

  const finalRows = await fetchServerFavorites(userId);

  const canonical: FavoriteItem[] = finalRows.map((r) => ({
    kind: r.kind as FavoriteItem["kind"],
    entityId: r.entity_id,
    slug: r.slug ?? undefined,
    title: r.title ?? undefined,
    imagePath: r.image_path ?? undefined,
    savedAt: r.created_at,
    deletedAt: null,
  }));

  saveLocalFavorites(canonical);
  setLastSyncAt(new Date().toISOString());

  return canonical;
}

/**
 * Resolve active favorites against currently public/visible targets.
 * Returns valid favorites plus pruned (unresolvable) favorites.
 */
export async function resolveFavoritesAgainstPublicTargets(
  items: FavoriteItem[]
): Promise<{ validItems: FavoriteItem[]; prunedItems: FavoriteItem[] }> {
  const activeItems = items.filter((item) => !item.deletedAt);
  if (activeItems.length === 0) return { validItems: [], prunedItems: [] };

  const placeIds = Array.from(new Set(activeItems.filter((item) => item.kind === "place").map((item) => item.entityId)));
  const producerIds = Array.from(new Set(activeItems.filter((item) => item.kind === "producer").map((item) => item.entityId)));
  const activityIds = Array.from(new Set(activeItems.filter((item) => item.kind === "activity").map((item) => item.entityId)));

  const [placeData, producerData] = await Promise.all([
    placeIds.length
      ? supabase
          .from("places_explore")
          .select("id,slug,name")
          .in("id", placeIds)
      : Promise.resolve({ data: [], error: null }),
    producerIds.length
      ? supabase
          .from("producers")
          .select("id,slug,name")
          .eq("status", "public")
          .not("published_at", "is", null)
          .in("id", producerIds)
      : Promise.resolve({ data: [], error: null }),
  ]);

  const placeMap = new Map(
    ((placeData.data || []) as Array<{ id: string; slug: string | null; name: string | null }>).map((row) => [row.id, row])
  );
  const producerMap = new Map(
    ((producerData.data || []) as Array<{ id: string; slug: string | null; name: string | null }>).map((row) => [row.id, row])
  );

  const activityMap = new Map<string, { title: string | null }>();
  await Promise.all(
    activityIds.map(async (eventId) => {
      const { data, error } = await supabase.rpc("get_public_events_with_bookings", {
        p_event_id: eventId,
        p_limit: 1,
      });
      if (error) return;
      const row = (Array.isArray(data) ? data[0] : data) as
        | { id?: string; title?: string | null; timing_mode?: string | null; start_date?: string | null; end_date?: string | null }
        | undefined;
      if (!row?.id) return;
      const isVisible = isActivityPubliclyVisible({
        timingMode: row.timing_mode || "date_time",
        startDate: row.start_date || null,
        endDate: row.end_date || null,
      });
      if (isVisible) {
        activityMap.set(eventId, { title: row.title ?? null });
      }
    })
  );

  const validItems: FavoriteItem[] = [];
  const prunedItems: FavoriteItem[] = [];

  for (const item of activeItems) {
    if (item.kind === "place") {
      const place = placeMap.get(item.entityId);
      if (!place) {
        prunedItems.push(item);
        continue;
      }
      validItems.push({
        ...item,
        slug: place.slug ?? item.slug,
        title: place.name ?? item.title,
      });
      continue;
    }

    if (item.kind === "producer") {
      const producer = producerMap.get(item.entityId);
      if (!producer) {
        prunedItems.push(item);
        continue;
      }
      validItems.push({
        ...item,
        slug: producer.slug ?? item.slug,
        title: producer.name ?? item.title,
      });
      continue;
    }

    const activity = activityMap.get(item.entityId);
    if (!activity) {
      prunedItems.push(item);
      continue;
    }
    validItems.push({
      ...item,
      slug: item.entityId,
      title: activity.title ?? item.title,
    });
  }

  return { validItems, prunedItems };
}

/**
 * Add or remove a single favorite on the server via RPC.
 */
export async function toggleServerFavorite(
  _userId: string,
  kind: string,
  entityId: string,
  _add: boolean,
  meta?: { slug?: string; title?: string; imagePath?: string }
): Promise<{ is_favorited: boolean; favorite_id: string | null }> {
  const { data, error } = await supabase.rpc("toggle_favorite", {
    p_kind: kind,
    p_entity_id: entityId,
    p_slug: meta?.slug ?? null,
    p_title: meta?.title ?? null,
    p_image_path: meta?.imagePath ?? null,
  });

  if (error) {
    console.error("toggle_favorite RPC error", error);
    throw error;
  }

  const row = Array.isArray(data) ? data[0] : data;
  return {
    is_favorited: row?.is_favorited ?? _add,
    favorite_id: row?.favorite_id ?? null,
  };
}
